// mounjaroCycle.js — Mounjaro (tirzepatide) weekly cycle awareness.
//
// Scans all supplement/meal entries in calorieLog for the last 14 days,
// looking for entries whose name matches /mounjaro|tirzepatide/i.
//
// Returns { lastDoseTs, dayOfCycle (1-7), nextDoseTs } or null if no dose
// found in the last 14 days.

const DOSE_WINDOW_MS = 14 * 24 * 3600 * 1000;
const CYCLE_DAYS     = 7;
const MOUNJARO_RE    = /mounjaro|tirzepatide/i;

// Most-recent Mounjaro dose timestamp from the dedicated Medications tracker
// (localStorage `sirat-medications`, history:[{ts}]). This is where the user
// actually logs the injection, so the cycle MUST read it — otherwise dosing
// there leaves the weekly-check reminder thinking no dose ever happened.
// Returns the latest ts within the dose window, or null.
export function lastMedsMounjaroTs(cutoff = Date.now() - DOSE_WINDOW_MS) {
  try {
    const meds = JSON.parse(localStorage.getItem("sirat-medications") || "[]");
    if (!Array.isArray(meds)) return null;
    let latest = null;
    for (const m of meds) {
      if (!MOUNJARO_RE.test(String(m?.name || ""))) continue;
      for (const h of (m.history || [])) {
        const ts = Number(h?.ts);
        if (Number.isFinite(ts) && ts >= cutoff && (!latest || ts > latest)) latest = ts;
      }
    }
    return latest;
  } catch { return null; }
}

// True if the user has EVER logged a Mounjaro dose in the Medications tracker
// (any time, ignoring the window) — used to decide whether reminders apply.
export function hasAnyMedsMounjaro() {
  try {
    const meds = JSON.parse(localStorage.getItem("sirat-medications") || "[]");
    if (!Array.isArray(meds)) return false;
    return meds.some(m => MOUNJARO_RE.test(String(m?.name || "")) && (m.history || []).length > 0);
  } catch { return false; }
}

export function getMounjaroDay(calorieLog) {
  if (!calorieLog || typeof calorieLog !== "object") calorieLog = {};

  const cutoff    = Date.now() - DOSE_WINDOW_MS;
  let lastDoseTs  = null;

  for (const [dateStr, dayLog] of Object.entries(calorieLog)) {
    const dayTs = Date.parse(`${dateStr}T12:00:00`);
    if (!Number.isFinite(dayTs) || dayTs < cutoff) continue;

    const meals = dayLog?.meals || {};
    for (const entries of Object.values(meals)) {
      if (!Array.isArray(entries)) continue;
      for (const e of entries) {
        if (!MOUNJARO_RE.test(String(e?.name || ""))) continue;
        // Prefer the entry's own loggedAt timestamp if present, else noon on the day.
        const ts = e.loggedAt ? Date.parse(e.loggedAt) : dayTs;
        if (Number.isFinite(ts) && (!lastDoseTs || ts > lastDoseTs)) lastDoseTs = ts;
      }
    }
  }

  // Also honour the Medications tracker — take whichever dose is more recent.
  const medsTs = lastMedsMounjaroTs(cutoff);
  if (medsTs && (!lastDoseTs || medsTs > lastDoseTs)) lastDoseTs = medsTs;

  if (!lastDoseTs) return null;

  const daysSince = (Date.now() - lastDoseTs) / 86400000;
  const dayOfCycle = Math.max(1, Math.min(CYCLE_DAYS, Math.ceil(daysSince)));
  const nextDoseTs = lastDoseTs + CYCLE_DAYS * 86400000;

  return { lastDoseTs, dayOfCycle, nextDoseTs };
}
